import { pick } from 'es-toolkit';

import type { SimpleObject } from '@clearverify/types';

import {
  type UserRegistrationVerifierAdminMutationVariables,
  type UserRegistrationVerifierManagerMutationVariables,
  VerifierType,
} from 'src/api';

import {
  CommercialVerifierBasicInfoFormFields,
  CommercialVerifierBasicInfoFormValues,
  EmailConfirmationFormFields,
  EmailConfirmationFormValues,
  GovernmentVerifierBasicInfoFormFields,
  GovernmentVerifierBasicInfoFormValues,
  InitialStepFormFields,
  InitialStepFormValues,
  RelatedVerifier,
  VerifierBasicInfoFormFields,
  VerifierBasicInfoFormValues,
} from '../types';

export const prepareFormDataVerifierAdmin = (
  formStepperData: SimpleObject,
): UserRegistrationVerifierAdminMutationVariables => {
  const isCommercialVerifier = formStepperData.accountType === 'Commercial';

  const userData = pick(
    formStepperData,
    Object.keys(InitialStepFormFields),
  ) as InitialStepFormValues;

  const emailConfirmationData = pick(
    formStepperData,
    Object.keys(EmailConfirmationFormFields),
  ) as EmailConfirmationFormValues;

  const commercialVerifierData = pick(
    formStepperData,
    Object.keys(CommercialVerifierBasicInfoFormFields),
  ) as CommercialVerifierBasicInfoFormValues;

  const governmentVerifierData = pick(
    formStepperData,
    Object.keys(GovernmentVerifierBasicInfoFormFields),
  ) as GovernmentVerifierBasicInfoFormValues;

  const verifierData = pick(
    formStepperData,
    Object.keys(VerifierBasicInfoFormFields),
  ) as VerifierBasicInfoFormValues;

  const currentCompany = formStepperData.currentCompany as RelatedVerifier | undefined;

  const data: UserRegistrationVerifierAdminMutationVariables = {
    user: {
      firstName: userData.firstName,
      lastName: userData.lastName,
      phoneNumber: userData.phoneNumber,
      password: userData.password,
      email: userData.email,
    },
    verifier: {
      type: isCommercialVerifier ? VerifierType.Commercial : VerifierType.Government,

      companyDisplayName: verifierData.companyDisplayInfo,
      contactEmail: verifierData.primaryContactEmail,
      contactPhone: verifierData.primaryContactPhone,
      commercialName: verifierData.commercialName,
      fein: verifierData.fein,
      industry: verifierData.industry.value,
      website: verifierData.websiteUrl,
      permissiblePurposeReferenceId: verifierData.permissiblePurposeReferenceId,
      companyId: currentCompany?.id,

      address: {
        street: String(verifierData.street),
        city: String(verifierData.city),
        zipCode: String(verifierData.zipCode),
        county: String(verifierData.county),
        state: String(verifierData.state),
      },

      ...(isCommercialVerifier
        ? commercialVerifierData
        : {
            governmentEntityName: governmentVerifierData.governmentEntityName,
            governmentType: governmentVerifierData.typeOfGovernment,
            governmentDepartment: governmentVerifierData.governmentDepartment,
          }),
    },

    ...emailConfirmationData,
  };

  return data;
};

export const prepareFormDataVerifierManager = (
  formStepperData: SimpleObject,
): UserRegistrationVerifierManagerMutationVariables => {
  const currentCompany = formStepperData.currentCompany as RelatedVerifier | undefined;

  const data: UserRegistrationVerifierManagerMutationVariables = {
    verifierId: currentCompany?.id ?? '',
    user: {
      email: formStepperData.email as string,
      firstName: formStepperData.firstName as string,
      lastName: formStepperData.lastName as string,
      password: String(formStepperData.password),
      phoneNumber: formStepperData.phoneNumber as string,
    },
    emailConfirmationCode: formStepperData.emailConfirmationCode as string,
  };

  return data;
};
